import { fromIzQuestion, toIzQuestion } from '../../question-bank/izteach';
import type { IzQuestion } from '../../question-bank/izteach';
import { attachGroupChildren, groupChildIds, toViewOnlyQuestion } from '../../question-bank/viewOnly';
import type { ViewOnlyQuestion } from '../../question-bank/viewOnly';
import type { ServerQuery } from '../../question-bank/paging';
import type { QuestionBankPayload } from '../../question-bank/payload';
import type { QuestionBankItem } from '../../question-bank/types';
import { adminQuestionRows as adminRepository, bankVendorId, questionRows as repository } from './question-bank-rows';

/**
 * /question on the IZTeach exam gateway - izteach-examination-service question/controller.js.
 *
 * The bank is tenant-wide: every question of the vendor, readable and editable by any teacher,
 * the same one the admin's /question-bank works on. Reads go to /question/admin because plain
 * GET /question strips `correct`, and the board needs the answer key. The vendor is the content
 * tenant's (question-bank-rows.ts), in the query of every call; create also needs it in the body,
 * because the controller reads it from there and the schema requires it.
 *
 * There is no batch endpoint: the store posts one question at a time.
 */

/** Built by question-bank/paging.ts; any key the model does not reserve is a filter on the row. */
export type QuestionBankQuery = ServerQuery;

/** A page as the server sent it, and how many of its rows the board can show. */
export interface QuestionBankPage {
  items: QuestionBankItem[];
  /** Types the board cannot edit, shown read-only; groups arrive with their children. */
  others: ViewOnlyQuestion[];
  /** Rows the server returned, shown or not - what paging has to count. */
  fetched: number;
}

/** Ids per id_in request, so a page of big groups stays well under the gateway's URL limit. */
const CHILD_CHUNK = 40;

/** Group children by id, in as few requests as the page allows; null when any of them failed. */
function fetchChildren(ids: string[]): Promise<IzQuestion[] | null> {
  if (ids.length === 0) return Promise.resolve([]);
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += CHILD_CHUNK) chunks.push(ids.slice(i, i + CHILD_CHUNK));
  return Promise.all(chunks.map((chunk) => adminRepository.fetch<IzQuestion[]>({ id_in: chunk, _limit: chunk.length })))
    .then((lists) => lists.reduce<IzQuestion[]>((all, list) => all.concat(Array.isArray(list) ? list : []), []))
    // a group without its children still lists its stem, so the page must not fail over them
    .catch(() => null);
}

function adopt(raw: IzQuestion): QuestionBankItem {
  const item = fromIzQuestion(raw);
  // the body we sent was a board type, so the echo can only fail to map if the server lost fields
  if (!item) throw new Error('Máy chủ trả về câu hỏi không đọc được.');
  return item;
}

/** Server rows as the board shows them: board types editable, the rest read-only with group children attached. */
function toPage(rows: IzQuestion[]): Promise<QuestionBankPage> {
  const items: QuestionBankItem[] = [];
  const others: ViewOnlyQuestion[] = [];
  rows.forEach((raw) => {
    const item = fromIzQuestion(raw);
    if (item) { items.push(item); return; }
    const other = toViewOnlyQuestion(raw);
    if (other) others.push(other);
  });
  return fetchChildren(groupChildIds(others)).then((children) => ({
    items,
    others: attachGroupChildren(others, children),
    fetched: rows.length,
  }));
}

export const QuestionBankRepository = {
  /** Raw rows, for paging that has to see them before they are mapped (question-bank/originalPage.ts). */
  rows: (query: QuestionBankQuery): Promise<IzQuestion[]> =>
    adminRepository.fetch<IzQuestion[]>(query).then((rows) => (Array.isArray(rows) ? rows : [])),

  toPage,

  list: (query: QuestionBankQuery): Promise<QuestionBankPage> => QuestionBankRepository.rows(query).then(toPage),

  // /question/count reads the same filters minus paging; with no type picked it includes types the board hides
  count: (filter: QuestionBankQuery) => repository.count(filter),

  create: (payload: QuestionBankPayload) =>
    repository
      .create<IzQuestion>({ ...toIzQuestion(payload, undefined), vendorId: bankVendorId() })
      .then(adopt),

  // PUT merges top-level fields, but config is one field, so the stored config is sent back whole
  update: (item: QuestionBankItem, payload: QuestionBankPayload) =>
    repository.update<IzQuestion>(item.id, toIzQuestion(payload, item.izConfig)).then(adopt),

  remove: (id: string) => repository.remove<unknown>(id),
};

export default QuestionBankRepository;
